<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useNotificationsStore, type Notification } from '@/stores/notifications'
import NavIcon from './NavIcon.vue'

/**
 * The bell, its badge, and the list behind it.
 *
 * The badge counts unread; the ring counts questions, because something
 * waiting on you is a different kind of urgent from something telling you
 * what happened. A panel rather than a page: answering an invitation should
 * not cost you the screen you were on.
 */
const notifications = useNotificationsStore()
const open = ref(false)
const busy = ref<string | null>(null)

const panel = ref<HTMLElement | null>(null)

async function toggle() {
  open.value = !open.value
  if (open.value) await notifications.load()
}

function closeOnOutside(event: MouseEvent) {
  if (!open.value) return
  if (panel.value && !panel.value.contains(event.target as Node)) open.value = false
}

onMounted(() => {
  document.addEventListener('click', closeOnOutside)
  void notifications.refreshCounts().catch(() => undefined)
})
onBeforeUnmount(() => document.removeEventListener('click', closeOnOutside))

const badge = computed(() => (notifications.unread > 9 ? '9+' : String(notifications.unread)))

/** Which invitation is being answered "yes, but…", and with how much. */
const revising = ref<{ id: string; servings: number } | null>(null)

async function answer(item: Notification, decision: 'accepted' | 'rejected', servings?: number) {
  busy.value = item.id
  try {
    await notifications.decide(item.id, decision, servings ?? null)
    revising.value = null
  } finally {
    busy.value = null
  }
}

/** Relative, because "3 minutes ago" is what a person actually wants. */
function when(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`
  return `${Math.floor(seconds / 86_400)}d ago`
}
</script>

<template>
  <div ref="panel" class="relative">
    <button
      class="relative flex h-9 w-9 items-center justify-center rounded-lg border"
      :class="notifications.pending > 0 ? 'border-zest bg-zest/10' : 'border-stone'"
      :aria-label="`Notifications${notifications.unread ? `, ${notifications.unread} unread` : ''}`"
      @click.stop="toggle"
    >
      <NavIcon name="bell" :size="18" />
      <span
        v-if="notifications.unread > 0"
        class="absolute -right-1 -top-1 min-w-4 rounded-full bg-tomato px-1 text-center text-[10px] font-semibold leading-4 text-paper"
      >
        {{ badge }}
      </span>
    </button>

    <div
      v-if="open"
      class="absolute right-0 z-50 mt-2 flex max-h-[70vh] w-80 max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl border border-stone bg-surface shadow-xl"
    >
      <div class="flex items-center justify-between border-b border-stone px-3 py-2">
        <span class="text-sm font-medium">Notifications</span>
        <div class="flex items-center gap-3">
          <button
            v-if="notifications.unread > 0"
            class="text-xs text-ink/60 underline"
            @click="notifications.markAllRead()"
          >
            Mark all read
          </button>
          <RouterLink to="/notifications" class="text-xs text-moss underline" @click="open = false">
            See all
          </RouterLink>
        </div>
      </div>

      <p v-if="notifications.loading" class="px-3 py-6 text-center text-sm text-ink/50">Loading…</p>
      <p v-else-if="notifications.error" class="px-3 py-6 text-center text-sm text-tomato">
        {{ notifications.error }}
      </p>
      <p v-else-if="notifications.list.length === 0" class="px-3 py-6 text-center text-sm text-ink/50">
        Nothing waiting for you.
      </p>

      <ul v-else class="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <li
          v-for="item in notifications.list"
          :key="item.id"
          class="border-b border-stone/60 px-3 py-2.5 last:border-0"
          :class="item.readAt ? '' : 'bg-mist/30'"
        >
          <div class="flex items-start gap-2">
            <span
              v-if="!item.readAt"
              class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-moss"
              aria-hidden="true"
            />
            <div class="min-w-0 flex-1">
              <p class="text-sm font-medium">{{ item.title }}</p>
              <p v-if="item.body" class="mt-0.5 text-sm text-ink/70">{{ item.body }}</p>
              <p class="mt-0.5 text-xs text-ink/50">
                <span v-if="item.actorName">{{ item.actorName }} · </span>{{ when(item.createdAt) }}
              </p>

              <!-- A question. Somebody is waiting on this. -->
              <div v-if="item.decision === 'pending'" class="mt-2 flex gap-2">
                <button
                  class="rounded-lg bg-moss px-3 py-1.5 text-xs font-medium text-paper disabled:opacity-50"
                  :disabled="busy === item.id"
                  @click="answer(item, 'accepted')"
                >
                  Accept
                </button>
                <button
                  v-if="item.kind === 'shared_meal'"
                  class="rounded-lg border border-stone px-3 py-1.5 text-xs disabled:opacity-50"
                  :disabled="busy === item.id"
                  @click="revising = revising?.id === item.id ? null : { id: item.id, servings: 1 }"
                >
                  Yes, but…
                </button>
                <button
                  class="rounded-lg border border-stone px-3 py-1.5 text-xs disabled:opacity-50"
                  :disabled="busy === item.id"
                  @click="answer(item, 'rejected')"
                >
                  No thanks
                </button>
              </div>
              <!-- "Yes, but I'll have half": the place is theirs to size. -->
              <div v-if="revising?.id === item.id" class="mt-2 flex items-center gap-2 text-xs">
                <label class="flex items-center gap-1">
                  <input v-model.number="revising.servings" type="number" min="0.25" max="20" step="0.25" inputmode="decimal" class="w-16 rounded-lg border border-stone px-2 py-1 text-center" />
                  {{ revising.servings === 1 ? 'portion' : 'portions' }}
                </label>
                <button
                  class="rounded-lg bg-moss px-3 py-1.5 font-medium text-paper disabled:opacity-50"
                  :disabled="busy === item.id || !(revising.servings > 0)"
                  @click="answer(item, 'accepted', revising.servings)"
                >
                  Accept this much
                </button>
              </div>
              <p v-else-if="item.decision" class="mt-1 text-xs text-ink/50">
                You {{ item.decision === 'accepted' ? 'accepted' : 'turned down' }} this.
              </p>

              <button
                v-else-if="!item.readAt"
                class="mt-1 text-xs text-ink/50 underline"
                @click="notifications.markRead(item.id)"
              >
                Mark read
              </button>
            </div>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>
